/**
 * Ticket Service
 * Handles Firestore ticket operations
 * 
 * Firestore Collection: 'tickets' (configurable via VUE_APP_TICKET_COLLECTION_NAME)
 * 
 * Document Structure:
 * - title: string (required)
 * - description: string (required)
 * - status: 'open' | 'in_progress' | 'resolved' | 'closed' (required)
 * - service: string (required) - e.g., 'AquaEdge', 'CarbonEdge', 'AgriEdge Master'
 * - createdBy: string (required) - user email
 * - priority: 'low' | 'medium' | 'high' | 'urgent' (required)
 * - platform: string (required) - Platform identifier, e.g., 'ASARI IRRIGATION PLATEFOLME'
 * - createdAt: Timestamp (required)
 * - updatedAt: Timestamp (required)
 * - assignedTo: string (optional) - assigned user email
 * 
 * Environment Variables (from .env):
 * - VUE_APP_TICKET_SERVICE_NAME: Default service name (default: 'AgriEdge Master')
 * - VUE_APP_TICKET_DEFAULT_PRIORITY: Default priority (default: 'medium')
 * - VUE_APP_TICKET_DEFAULT_STATUS: Default status (default: 'open')
 * - VUE_APP_TICKET_COLLECTION_NAME: Firestore collection name (default: 'tickets')
 * - VUE_APP_TICKET_ASSIGNED_TO_EMAIL: Default assigned email (optional)
 * - VUE_APP_TICKET_MAX_RESULTS: Max results for queries (default: 50)
 */
import { collection, addDoc, serverTimestamp, updateDoc, doc, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '@/firebase';

// Get configuration from environment variables
const getTicketConfig = () => ({
  serviceName: process.env.VUE_APP_TICKET_SERVICE_NAME || 'AgriEdge Master',
  defaultPriority: process.env.VUE_APP_TICKET_DEFAULT_PRIORITY || 'medium',
  defaultStatus: process.env.VUE_APP_TICKET_DEFAULT_STATUS || 'open',
  collectionName: process.env.VUE_APP_TICKET_COLLECTION_NAME || 'tickets',
  assignedToEmail: process.env.VUE_APP_TICKET_ASSIGNED_TO_EMAIL || null,
  maxResults: parseInt(process.env.VUE_APP_TICKET_MAX_RESULTS || '50', 10),
});

export const ticketService = {
  /**
   * Create a new ticket in Firestore
   * 
   * @param {Object} ticketData - Ticket data
   * @param {string} ticketData.title - Ticket title
   * @param {string} ticketData.description - Ticket description
   * @param {string} ticketData.service - Service name (default: from VUE_APP_TICKET_SERVICE_NAME)
   * @param {string} ticketData.createdBy - User email who created the ticket
   * @param {string} ticketData.priority - Priority level (default: from VUE_APP_TICKET_DEFAULT_PRIORITY)
   * @param {string} ticketData.status - Status (default: from VUE_APP_TICKET_DEFAULT_STATUS)
   * @param {string} ticketData.assignedTo - Optional assigned user email (default: from VUE_APP_TICKET_ASSIGNED_TO_EMAIL)
   * @returns {Promise<string>} Document ID of created ticket
   */
  async createTicket(ticketData) {
    try {
      const config = getTicketConfig();
      
      // Validate required fields
      if (!ticketData.title || !ticketData.description || !ticketData.createdBy) {
        throw new Error('Title, description, and createdBy are required fields');
      }

      // Prepare ticket document with environment variable defaults
      const ticket = {
        title: ticketData.title.trim(),
        description: ticketData.description.trim(),
        status: ticketData.status || config.defaultStatus,
        service: ticketData.service || config.serviceName,
        createdBy: ticketData.createdBy.trim(),
        priority: ticketData.priority || config.defaultPriority,
        platform: ticketData.platform || 'ASARI IRRIGATION PLATEFOLME', // Platform identifier
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Add assignedTo from parameter, env var, or skip
      if (ticketData.assignedTo) {
        ticket.assignedTo = ticketData.assignedTo.trim();
      } else if (config.assignedToEmail) {
        ticket.assignedTo = config.assignedToEmail.trim();
      }

      // Validate status
      const validStatuses = ['open', 'in_progress', 'resolved', 'closed'];
      if (!validStatuses.includes(ticket.status)) {
        ticket.status = 'open';
      }

      // Validate priority
      const validPriorities = ['low', 'medium', 'high', 'urgent'];
      if (!validPriorities.includes(ticket.priority)) {
        ticket.priority = 'medium';
      }

      // Add document to Firestore (using collection name from env)
      console.log('Attempting to add ticket to Firestore...');
      console.log('Collection name:', config.collectionName);
      console.log('Ticket data to save:', ticket);
      console.log('Firestore db instance:', db);
      
      try {
        const ticketsCollection = collection(db, config.collectionName);
        console.log('Collection reference created:', ticketsCollection);
        
        console.log('Calling addDoc...');
        const docRef = await addDoc(ticketsCollection, ticket);
        
        console.log('✅ Ticket created successfully with ID:', docRef.id);
        return docRef.id;
      } catch (firestoreError) {
        console.error('❌ Firestore addDoc error:', firestoreError);
        console.error('Error code:', firestoreError.code);
        console.error('Error message:', firestoreError.message);
        console.error('Full error:', firestoreError);
        throw firestoreError;
      }
    } catch (error) {
      console.error('❌ Error creating ticket:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error);
      throw error;
    }
  },

  /**
   * Update ticket status
   * 
   * @param {string} ticketId - Firestore document ID
   * @param {Object} updates - Fields to update
   * @param {string} updates.status - New status (optional)
   * @param {string} updates.assignedTo - Assigned user (optional)
   * @returns {Promise<void>}
   */
  async updateTicket(ticketId, updates) {
    try {
      const config = getTicketConfig();
      const ticketRef = doc(db, config.collectionName, ticketId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Validate status if provided
      if (updates.status) {
        const validStatuses = ['open', 'in_progress', 'resolved', 'closed'];
        if (!validStatuses.includes(updates.status)) {
          throw new Error(`Invalid status: ${updates.status}`);
        }
      }

      // Validate priority if provided
      if (updates.priority) {
        const validPriorities = ['low', 'medium', 'high', 'urgent'];
        if (!validPriorities.includes(updates.priority)) {
          throw new Error(`Invalid priority: ${updates.priority}`);
        }
      }

      await updateDoc(ticketRef, updateData);
      console.log('Ticket updated:', ticketId);
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  },

  /**
   * Get tickets by user email
   * 
   * @param {string} userEmail - User email
   * @param {number} maxResults - Maximum number of results (default: 50)
   * @returns {Promise<Array>} Array of ticket documents
   */
  async getTicketsByUser(userEmail, maxResults = null) {
    try {
      const config = getTicketConfig();
      const ticketsRef = collection(db, config.collectionName);
      const limitValue = maxResults || config.maxResults;
      const q = query(
        ticketsRef,
        where('createdBy', '==', userEmail),
        orderBy('createdAt', 'desc'),
        limit(limitValue)
      );

      const querySnapshot = await getDocs(q);
      const tickets = [];
      
      querySnapshot.forEach((doc) => {
        tickets.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return tickets;
    } catch (error) {
      console.error('Error getting tickets:', error);
      throw error;
    }
  },

  /**
   * Create ticket from error context
   * Automatically formats error information into a ticket
   * 
   * @param {Object} errorContext - Error context from useErrorHandler
   * @param {string} userEmail - User email
   * @param {string} service - Service name (optional)
   * @returns {Promise<string>} Document ID of created ticket
   */
  async createTicketFromError(errorContext, userEmail, service = null) {
    try {
      const config = getTicketConfig();
      const serviceName = service || config.serviceName;
      
      // Determine priority based on error status
      let priority = config.defaultPriority;
      if (errorContext.status >= 500) {
        priority = 'high';
      }
      if (errorContext.status === 503 || errorContext.status === 504) {
        priority = 'urgent';
      }

      // Format title
      const title = errorContext.message 
        ? `Erreur: ${errorContext.message.substring(0, 100)}`
        : 'Erreur système';

      // Format description with error details
      let description = `Une erreur s'est produite dans l'application.\n\n`;
      description += `**Détails de l'erreur:**\n`;
      description += `- Message: ${errorContext.message || 'N/A'}\n`;
      description += `- Statut HTTP: ${errorContext.status || 'N/A'} ${errorContext.statusText || ''}\n`;
      description += `- URL: ${errorContext.url || 'N/A'}\n`;
      description += `- Méthode: ${errorContext.method || 'N/A'}\n`;
      description += `- Date: ${errorContext.timestamp ? new Date(errorContext.timestamp).toLocaleString('fr-FR') : 'N/A'}\n`;
      description += `- Page: ${errorContext.currentPath || 'N/A'}\n`;
      
      if (errorContext.userAgent) {
        description += `- Navigateur: ${errorContext.userAgent}\n`;
      }

      return await this.createTicket({
        title,
        description,
        service: serviceName,
        createdBy: userEmail,
        priority,
        status: config.defaultStatus,
        platform: 'ASARI IRRIGATION PLATEFOLME', // Platform identifier
      });
    } catch (error) {
      console.error('Error creating ticket from error context:', error);
      throw error;
    }
  },
};

